//#region import libraries
import React, { useEffect, useMemo, useState } from 'react';
import { MdFlight } from 'react-icons/md';
import { FaClock } from 'react-icons/fa6';

// bootstrap
import { Card as BootstrapCard } from 'react-bootstrap';

// redux
import { useSelector } from "react-redux";
import { selectUsedTheme } from '_redux/slices/timeline/theme';
import { selectThirtyMinLayout, selectLocationsTableView } from "_redux/slices/timeline/timeline-control";
import { selectSelectedTask, selectSelectedTasksList, selectSelectedMultiplePAXTask } from "_redux/slices/timeline/selected-task";
import { selectAllTimelineAreas } from '_redux/slices/timeline/timeline-areas';
import { selectAdhocTaskList } from '_redux/slices/timeline/timeline-data';

// styling
import { Spacing } from '_styles';

// custom components
import JobCard from '_scenes/portal/timeline-v2/job-components/job-card';
import { CustomSubtitle, CustomMetric, CollapsibleComponent } from '_atoms';
import { CollapsibleChevron } from '_atoms/container/collapsable';
import LocationDetailView from './location-detail-view';
import AdhocTaskTableRow from './adhoc-task-table-row';

//#endregion

// card used to display location information in airport locations page
const NewAirportLocationCard = (props) => {

    //#region redux
    const usedTheme = useSelector(selectUsedTheme);
    const thirtyMinLayout = useSelector(selectThirtyMinLayout);
    const showDetail = useSelector(selectLocationsTableView);
    const selectedTask = useSelector(selectSelectedTask);
    const selectedTasksList = useSelector(selectSelectedTasksList);
    const selectedMultiplePAXTask = useSelector(selectSelectedMultiplePAXTask);
    const timelineAreas = useSelector(selectAllTimelineAreas);
    const adhocTaskList = useSelector(selectAdhocTaskList);
    //#endregion

    //#region state
    const [tableData, setTableData] = useState([]);
    const [passengerCount, setPassengerCount] = useState(0);
    const [locationTitle, setLocationTitle] = useState('Location Title');
    const [locationAreaTitles, setLocationAreaTitles] = useState([]);
    const [locationColor, setLocationColor] = useState(false);
    const [expanded, setExpanded] = useState(true);
    const [adhocExpanded, setAdhocExpanded] = useState(false);
    const [sortMode, setSortMode] = useState('waiting');
    //#endregion

    //#region adhoc tasks
    const locationAdhocTasks = useMemo(() => {
        const locationId = props.location?.locationId;
        if (!locationId || !Array.isArray(adhocTaskList) || adhocTaskList.length === 0) return [];
        const locationIds = [locationId, ...(props.location?.mergedLocationIds ?? [])];
        return adhocTaskList
            .filter(t =>
                locationIds.includes(t?.location?._id) &&
                !t?.completed &&
                !t?.cancelled
            )
            .sort((a, b) => {
                const aTime = a?.assignedTime || '';
                const bTime = b?.assignedTime || '';
                if (aTime < bTime) return -1;
                if (aTime > bTime) return 1;
                return 0;
            });
    }, [adhocTaskList, props.location?.locationId, props.location?.mergedLocationIds]);

    const adhocCount = locationAdhocTasks.length;

    useEffect(() => {
        if (adhocCount === 0 && adhocExpanded) {
            setAdhocExpanded(false);
        }
    }, [adhocCount, adhocExpanded]);
    //#endregion

    //#region useEffect
    useEffect(() => {
        if (props.location) {
            // location name
            if (props.location.locationName) {
                setLocationTitle(props.location.locationName);
            }

            // location areas[]
            if (props.location?.locationAreaId?.length) {
                let locationAreaIds = props.location?.locationAreaId;

                if (Array.isArray(locationAreaIds)) {
                    const matchedAreas = locationAreaIds.map(id =>
                        timelineAreas.find(area => id === area?._id)?.title
                    );
                    setLocationAreaTitles(matchedAreas);
                }
            }

            // sort the pax array and display
            if (props.location.paxArray && props.location.paxArray.length > 0) {
                let paxArray = [...props.location.paxArray]
                paxArray = sortByDepartureTime(paxArray)
                const totalPax = paxArray.length;
                setPassengerCount(totalPax);
                setTableData(paxArray);
            } else {
                setPassengerCount(0);
                setTableData([]);
            }

            // location color
            if (props.location.color) {
                setLocationColor(props.location.color);
            }
        }
    }, [props.location]);

    //#endregion

    //#region Helper Functions

    // sorting paxArray: RC pax (paxManualCheckIn) always pinned first, then by departure time
    const sortByDepartureTime = (array) => {
        return array.sort((a, b) => {
            const aIsRC = !!(a?.paxManualCheckIn || a?.booking?.paxManualCheckIn || a?.originalBookingDoc?.paxManualCheckIn);
            const bIsRC = !!(b?.paxManualCheckIn || b?.booking?.paxManualCheckIn || b?.originalBookingDoc?.paxManualCheckIn);
            if (aIsRC && !bIsRC) return -1;
            if (!aIsRC && bIsRC) return 1;
            const timeA = new Date(a?.flightConfirmationDetails?.departureTime || a?.flightInformation?.flightTime);
            const timeB = new Date(b?.flightConfirmationDetails?.departureTime || b?.flightInformation?.flightTime);
            return timeA - timeB;
        });
    };
    //#endregion

    return (
        <div style={{ minHeight: 100, height: '100%', padding: Spacing.SCALE_8 }}>

            <BootstrapCard
                style={{
                    height: 'auto',
                    width: '100%',
                    backgroundColor: usedTheme.BACKGROUND_COLOR,
                    borderColor: usedTheme.BORDER_COLOR,
                    color: usedTheme.TEXT_COLOR,
                }}
            >
                {/* title + passenger count */}
                <div
                    className="border-bottom bg-light-subtle position-relative"
                    onClick={() => passengerCount > 0 && setExpanded(!expanded)}
                    style={{
                        padding: Spacing.SCALE_8,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: passengerCount > 0 ? 'pointer' : 'default',
                    }}
                >
                    {/* location color */}
                    {locationColor && (
                        <div
                            style={{
                                position: 'absolute',
                                top: Spacing.SCALE_8,
                                right: Spacing.SCALE_8,
                                width: 10,
                                height: 10,
                                backgroundColor: locationColor,
                                borderRadius: '50%',
                            }}
                        />
                    )}

                    {/* centered content */}
                    <div>
                        {/* location passenger count */}
                        <div>
                            <span style={{ fontSize: '12px', color: '#999' }}>{passengerCount} Passenger{passengerCount !== 1 ? 's' : ''}</span>
                        </div>
                        <div>
                            <span data-tippy-content="Location Name" style={{ fontSize: '1.1rem', fontWeight: 600, color: '#000' }}>
                                {locationTitle}
                            </span>
                        </div>
                    </div>

                    {/* sort toggle + collapse toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {passengerCount > 0 && showDetail && (
                            <div
                                onClick={e => e.stopPropagation()}
                                style={{ display: 'flex', gap: 3 }}
                            >
                                <button
                                    onClick={() => setSortMode('flight')}
                                    title="Sort by flight time"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        width: 24, height: 24, border: 'none', borderRadius: 4, cursor: 'pointer',
                                        backgroundColor: sortMode === 'flight' ? '#e0f2fe' : 'transparent',
                                        opacity: sortMode === 'flight' ? 1 : 0.4,
                                        padding: 0,
                                        transition: 'background-color 0.15s ease, opacity 0.15s ease',
                                    }}
                                >
                                    <MdFlight size={14} color="#1e40af" />
                                </button>
                                <button
                                    onClick={() => setSortMode('waiting')}
                                    title="Sort by waiting time"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        width: 24, height: 24, border: 'none', borderRadius: 4, cursor: 'pointer',
                                        backgroundColor: sortMode === 'waiting' ? '#e0f2fe' : 'transparent',
                                        opacity: sortMode === 'waiting' ? 1 : 0.4,
                                        padding: 0,
                                        transition: 'background-color 0.15s ease, opacity 0.15s ease',
                                    }}
                                >
                                    <FaClock size={12} color="#1e40af" />
                                </button>
                            </div>
                        )}
                        {passengerCount > 0 && (
                            <CollapsibleChevron isExpanded={expanded} onToggle={(e) => { e.stopPropagation(); setExpanded(!expanded); }} size={14} />
                        )}
                    </div>

                </div>

                {/* collapsable passenger list */}
                <CollapsibleComponent collapsable={passengerCount > 0} expanded={expanded} onToggle={() => setExpanded(!expanded)} hideChevron>
                    {showDetail ? (
                        <LocationDetailView locationId={props.location?.locationId} usedTheme={usedTheme} sortMode={sortMode} />
                    ) : (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: Spacing.SCALE_8,
                                marginBottom: Spacing.SCALE_8,
                            }}
                        >
                            {tableData.map((passenger, index) => (
                                <JobCard
                                    key={index}
                                    index={index}
                                    job={passenger}
                                    thirtyMinLayout={false}
                                    selectedTask={selectedTask}
                                    selectedTasksList={selectedTasksList}
                                    usedTheme={usedTheme}
                                    selectedMultiplePAXTask={selectedMultiplePAXTask}
                                    showFullCard={true}
                                    onCardClick={() => { console.log('card clicked...') }}
                                    fromLocationPanel={true}
                                    disabled={passenger?.paxPositionInfo?.activity === "Assigned"}
                                />
                            ))}
                        </div>
                    )}
                </CollapsibleComponent>

                {/* adhoc tasks section header */}
                {adhocCount > 0 && (
                    <div
                        className="border-top bg-light-subtle"
                        onClick={() => adhocCount > 0 && setAdhocExpanded(!adhocExpanded)}
                        style={{
                            padding: Spacing.SCALE_8,
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: adhocCount > 0 ? 'pointer' : 'default',
                        }}
                    >
                        <span
                            style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: adhocCount > 0 ? '#000' : '#999',
                            }}
                        >
                            Adhoc Tasks ({adhocCount} Active)
                        </span>
                        <CollapsibleChevron
                            isExpanded={adhocExpanded}
                            onToggle={(e) => { e.stopPropagation(); setAdhocExpanded(!adhocExpanded); }}
                            size={14}
                        />
                    </div>
                )}

                {/* collapsable adhoc tasks list */}
                <CollapsibleComponent
                    collapsable={adhocCount > 0}
                    expanded={adhocExpanded}
                    onToggle={() => setAdhocExpanded(!adhocExpanded)}
                    hideChevron
                >
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={adhocThStyles(usedTheme)}>Task Type</th>
                                    <th style={adhocThStyles(usedTheme)}>Employee</th>
                                    <th style={adhocThStyles(usedTheme)}>Assigned Time</th>
                                    <th style={adhocThStyles(usedTheme)}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {locationAdhocTasks.map((task) => (
                                    <AdhocTaskTableRow key={task?._id} task={task} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CollapsibleComponent>

            </BootstrapCard>

        </div>
    );
};

const adhocThStyles = (usedTheme) => ({
    padding: `${Spacing.SCALE_4}px ${Spacing.SCALE_8}px`,
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    borderBottom: `1px solid ${usedTheme?.BORDER_COLOR || 'lightgrey'}`,
    whiteSpace: 'nowrap',
});

export default NewAirportLocationCard;
